import { create } from 'zustand'
import { persist } from 'zustand/middleware'

export type Theme = 'light' | 'dark' | 'system'

export interface UndoAction {
  id: string
  summary: string
  timestamp: number
  revert: () => void
}

interface UiState {
  sidebarOpen: boolean
  chatOpen: boolean
  focusMode: boolean
  theme: Theme
  activityPanelOpen: boolean
  selectedTaskId: string | null
  undoActions: UndoAction[]

  // Actions
  toggleSidebar: () => void
  setSidebarOpen: (open: boolean) => void
  toggleChat: () => void
  setChatOpen: (open: boolean) => void
  toggleFocusMode: () => void
  setFocusMode: (on: boolean) => void
  setTheme: (theme: Theme) => void
  toggleActivityPanel: () => void
  setActivityPanelOpen: (open: boolean) => void
  setSelectedTaskId: (id: string | null) => void
  addUndoAction: (action: Omit<UndoAction, 'timestamp'>) => void
  removeUndoAction: (id: string) => void
  executeUndo: (id: string) => void
}

function applyTheme(theme: Theme) {
  const root = document.documentElement
  if (theme === 'dark' || (theme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches)) {
    root.classList.add('dark')
  } else {
    root.classList.remove('dark')
  }
}

export const useUiStore = create<UiState>()(
  persist(
    (set, get) => ({
      sidebarOpen: true,
      chatOpen: false,
      focusMode: false,
      theme: 'light',
      activityPanelOpen: false,
      selectedTaskId: null,
      undoActions: [],

      toggleSidebar: () => set((state) => ({ sidebarOpen: !state.sidebarOpen })),
      setSidebarOpen: (open) => set({ sidebarOpen: open }),
      toggleChat: () => set((state) => ({ chatOpen: !state.chatOpen })),
      setChatOpen: (open) => set({ chatOpen: open }),
      toggleFocusMode: () =>
        set((state) => ({
          focusMode: !state.focusMode,
          // In focus mode, always show chat and hide sidebar
          chatOpen: !state.focusMode ? true : state.chatOpen,
          sidebarOpen: !state.focusMode ? false : state.sidebarOpen,
        })),
      setFocusMode: (on) =>
        set({
          focusMode: on,
          chatOpen: on ? true : false,
          sidebarOpen: on ? false : true,
        }),
      setTheme: (theme) => {
        applyTheme(theme)
        set({ theme })
      },
      toggleActivityPanel: () => set((state) => ({ activityPanelOpen: !state.activityPanelOpen })),
      setActivityPanelOpen: (open) => set({ activityPanelOpen: open }),
      setSelectedTaskId: (id) => set({ selectedTaskId: id }),
      addUndoAction: (action) =>
        set((state) => ({
          undoActions: [
            { ...action, timestamp: Date.now() },
            ...state.undoActions,
          ].slice(0, 2), // Max 2 stacked
        })),
      removeUndoAction: (id) =>
        set((state) => ({
          undoActions: state.undoActions.filter((a) => a.id !== id),
        })),
      executeUndo: (id) => {
        const action = get().undoActions.find((a) => a.id === id)
        if (action) {
          action.revert()
          set((state) => ({
            undoActions: state.undoActions.filter((a) => a.id !== id),
          }))
        }
      },
    }),
    {
      name: 'copilot-ui-state',
      partialize: (state) => ({ focusMode: state.focusMode, theme: state.theme }),
      onRehydrateStorage: () => {
        // Apply theme on rehydration
        return (rehydratedState) => {
          if (rehydratedState?.theme) {
            applyTheme(rehydratedState.theme)
          }
        }
      },
    },
  ),
)

// Listen for system theme changes
if (typeof window !== 'undefined') {
  window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', () => {
    const { theme } = useUiStore.getState()
    if (theme === 'system') {
      applyTheme('system')
    }
  })
}
